/**
 * 開通失敗的**分類**：把後端／網路的原始錯誤分成四種使用者真的能動作的情況。
 *
 * 為什麼要這一支：`usePoolAccount` 以前一律說「原因顯示在上方」，但上方那條
 * 「登入已過期」是**另一個請求**的錯，兩者的因果關係是前端自己編的；使用者照著那句話
 * 去重新登入，回來還是同一顆帳號失敗。
 *
 * 規則：只根據**這次開通自己的錯誤字串**判斷，分不出來就說分不出來（`unknown`），
 * 不要往任何方向猜（不猜「在上方」、不猜「站方擋人」）。
 */
import { errorKindOf } from '@chat-rea/console-ui';

export type OpeningFailureKind = 'session-expired' | 'missing-credential' | 'protocol' | 'unknown';

/** 登入／授權過期（console 自己的 session，不是這顆帳號）。 */
const SESSION_PATTERN =
  /unauthorized|forbidden|unauthenticated|未授權|沒有權限|請重新登入|重新登入|登入已過期|登入.*過期|session.*過期|token.*(失效|過期)/i;

/** 這顆帳號的憑證本身有問題（池裡沒憑證、密碼錯、帳號不存在）。 */
const CREDENTIAL_PATTERN =
  /憑證|credential|password|密碼|帳號池|不存在於池|not\s*found.*(account|pool)|no\s*(token|credential)|缺.*(帳號|憑證)/i;

/** 站方 protocol 層的回應看不懂（封包格式、未知 op、解析失敗）。 */
const PROTOCOL_PATTERN =
  /protocol|protocol[_ ]?error|unexpected|unknown[_ ]op|未知.*(op|操作|指令)|封包|解析|parse|JSON|格式不符|不支援/i;

/**
 * 分類一次開通失敗。`raw` 是這次操作自己的錯誤訊息（沒有就傳 null）。
 * 判斷順序：先看能不能用既有的 `errorKind()` 判出授權，再看字樣，最後才是 unknown。
 */
export function classifyOpeningFailure(raw: string | null | undefined): OpeningFailureKind {
  const message = typeof raw === 'string' ? raw.trim() : '';
  if (message === '') return 'unknown';
  if (SESSION_PATTERN.test(message)) return 'session-expired';
  // `errorKindOf` 對 401／403 與「請重新登入」也回 unauthorized：兩邊判同一件事。
  if (errorKindOf(message) === 'unauthorized') return 'session-expired';
  if (CREDENTIAL_PATTERN.test(message)) return 'missing-credential';
  if (PROTOCOL_PATTERN.test(message)) return 'protocol';
  return 'unknown';
}

/** 每一種失敗對應的一句話：講清楚「這是誰的問題、下一步做什麼」。 */
const FAILURE_TEXT: Record<OpeningFailureKind, (username: string) => string> = {
  'session-expired': (username) =>
    `開通 ${username} 失敗：登入已過期，請先重新登入（不是這顆帳號的問題）`,
  'missing-credential': (username) => `開通 ${username} 失敗：這顆帳號缺憑證，要先去補`,
  protocol: (username) => `開通 ${username} 失敗：站方回了看不懂的回應（protocol error），可按重試`,
  unknown: (username) => `開通 ${username} 失敗：沒拿到具體原因，可按「重試」或看 log`,
};

/** 對使用者講的那一句；不含任何「原因在上方」這種推測。 */
export function describeOpeningFailure(
  username: string,
  raw: string | null | undefined,
): string {
  return FAILURE_TEXT[classifyOpeningFailure(raw)](username);
}
